
import { Component, Vue, Watch } from 'vue-property-decorator';
import AppHeader from '@/components/layout/AppHeader.vue';
import AppFooter from '@/components/layout/AppFooter.vue';
import AppFormOwnerCheck from '@/components/layout/AppFormOwnerCheck.vue';
import FormStore from '@/store/formStore';
import BasicInfoSummary from '@/components/layout/BasicInfoSummary.vue';
import { getModule } from 'vuex-module-decorators';
import AppNewForm from '@/components/layout/AppNewForm.vue';
import OfferConfirmationDialog from '@/components/dialogs/OfferConfirmationDialog.vue';
import IrregularOfferConfirmationDialog from '@/components/dialogs/IrregularOfferConfirmationDialog.vue';
import { RouteNames } from './router/types';
import {
  Offer, FormState, FormType, IrregularOffer,
} from './store/types';

@Component({
  components: {
    OfferConfirmationDialog,
    IrregularOfferConfirmationDialog,
    AppHeader,
    AppFooter,
    AppFormOwnerCheck,
    BasicInfoSummary,
    AppNewForm,
  },
})
export default class UniLoanForm extends Vue {
  // VUEX MODULES
  formStore: FormStore | null = null;

  // DATA
  FormState = FormState;

  RouteNames = RouteNames;

  // COMPUTED
  get offerDraft() {
    return this.formStore?.offerDraft || null;
  }

  get irregularOfferDraft() {
    return this.formStore?.irregularOfferDraft || null;
  }

  created() {
    this.formStore = getModule(FormStore);
  }

  @Watch('$route', { deep: true })
  async watchQuery() {
    if (this.$route.fullPath === '/') {
      this.formStore.startNewForm();
      return;
    }

    // Just for first load
    if (!this.formStore.loaded) {
      this.formStore.startInitialLoad();

      // Set data from query
      if (this.$route.query?.token && !this.formStore.token) this.formStore.setToken(this.$route.query?.token as string);
      if (this.$route.query?.type && !this.formStore.type) this.formStore.setType(this.$route.query?.type as FormType);
      if (this.$route.query?.utm_source && !this.formStore.utmParams.utmSource) this.formStore.setUTMParam({ key: 'utmSource', value: this.$route.query?.utm_source as string });
      if (this.$route.query?.utm_campaign && !this.formStore.utmParams.utmCampaign) this.formStore.setUTMParam({ key: 'utmCampaign', value: this.$route.query?.utm_campaign as string });
      if (this.$route.query?.utm_medium && !this.formStore.utmParams.utmMedium) this.formStore.setUTMParam({ key: 'utmMedium', value: this.$route.query?.utm_medium as string });

      // Get form by token if there is one
      if (this.formStore.token) {
        // Apply offer to the form
        if (this.$route.query?.offer) {
          const offer: Offer = JSON.parse(atob(this.$route.query.offer as string));

          if (!offer.value || !offer.installment_value) throw new Error('Offer is not in valid format');

          // Wait until the offer be confirmed in OfferConfirmationDialog
          this.formStore.setOfferDraft(offer);
        }

        if (this.$route.query?.irregular_offer) {
          const offers: IrregularOffer = JSON.parse(atob(this.$route.query.irregular_offer as string));

          if (!offers.claim_group_1) throw new Error('Offer is not in valid format');

          // Wait until the offer be confirmed in OfferConfirmationDialog
          this.formStore.setIrregularOfferDraft(offers);
        }

        // Get form by token and go to the right route
        try {
          await this.formStore.getForm();

          // Check if the form is closed
          if (this.formStore.formState === FormState.Closed) {
            this.formStore.startNewForm();
            if (this.$route.name !== RouteNames.Crossroad) this.$router.push({ name: RouteNames.Crossroad });
          }

          if ((this.formStore.token && this.$route.name === RouteNames.Documents) || this.formStore.offerDraft) return;

          this.moveToActiveStep();
        } catch (_) {
          this.$toast.error(this.$t('formsApi.errors.application'));
        }
      } else {
        // If there is no token start the new form
        this.formStore.startNewForm();
      }

      // Mark intial load as done
      this.formStore.finishInitialLoading();
      this.formStore.stopInitialLoad();
    }
  }

  async onOfferDraftConfirm(): Promise<void> {
    if (!this.formStore.offerDraft) return;

    try {
      await this.formStore.applyOffer(this.formStore.offerDraft);

      this.formStore.setIsOffer(true);

      this.formStore.setOfferDraft(null);

      this.formStore.getForm();

      this.moveToActiveStep();
    } catch (e) {
      const { fake_offer: fakeOffer } = await e.json?.() ?? {};

      if (fakeOffer) this.$toast.error(this.$t('formsApi.errors.fakeOffer').toString());
      else this.$toast.error(this.$t('formsApi.errors.offer').toString());
    }
  }

  async onIrregularOfferDraftConfirm(): Promise<void> {
    if (!this.formStore.irregularOfferDraft) return;

    try {
      await this.formStore.applyIrregularOffer(this.formStore.irregularOfferDraft);

      this.formStore.setIsOffer(true);

      this.formStore.setIrregularOfferDraft(null);

      this.formStore.getForm();

      this.moveToActiveStep();
    } catch (e) {
      const { fake_offer: fakeOffer } = await e.json?.() ?? {};

      if (fakeOffer) this.$toast.error(this.$t('formsApi.errors.fakeOffer').toString());
      else this.$toast.error(this.$t('formsApi.errors.offer').toString());
    }
  }

  moveToActiveStep() {
    if (this.formStore.formState === null
      && this.$route.name !== this.formStore.noStateRoute.name) {
      this.$router.push(this.formStore.noStateRoute);
    } else if (this.formStore.formState === FormState.BasicDealInfo
      && this.$route.name !== this.formStore.basicDealInfoRoute.name) {
      this.$router.push(this.formStore.basicDealInfoRoute);
    } else if (this.formStore.formState === FormState.PersonInfo
      && this.$route.name !== this.formStore.bankingInitRoute.name) {
      this.$router.push(this.formStore.bankingInitRoute);
    } else if (this.formStore.formState === FormState.BankingInfo
      && this.$route.name !== this.formStore.bankingInfoRoute.name) {
      this.$router.push(this.formStore.bankingInfoRoute);
    } else if (this.formStore.formState === FormState.CollateralInfo
      && this.$route.name !== this.formStore.collateralInfoRoute.name) {
      this.$router.push(this.formStore.collateralInfoRoute);
    }
  }
}
